import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '../../lib/cn';
import type { ResourceKey, Severity } from '../../lib/game';

export type BadgeAccent = ResourceKey | Severity | 'neutral';
export type BadgeVariant = 'soft' | 'outline' | 'solid';

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  accent?: BadgeAccent;
  variant?: BadgeVariant;
  size?: 'sm' | 'md';
  icon?: ReactNode;
  /** Adds a pulsing dot — for live states like "under attack" or "researching". */
  pulse?: boolean;
}

/**
 * Accent lookup.
 *
 * Written as full class strings rather than interpolated names because
 * Tailwind's scanner only sees literals — `bg-${accent}/15` would be purged.
 */
const ACCENTS: Record<BadgeAccent, Record<BadgeVariant, string>> = {
  titan: {
    soft: 'bg-titan/12 text-titan-bright border-titan/30',
    outline: 'bg-transparent text-titan border-titan/55',
    solid: 'bg-titan text-content-inverse border-titan',
  },
  silicate: {
    soft: 'bg-silicate/12 text-silicate-bright border-silicate/30',
    outline: 'bg-transparent text-silicate border-silicate/55',
    solid: 'bg-silicate text-content-inverse border-silicate',
  },
  isotope: {
    soft: 'bg-isotope/12 text-isotope-bright border-isotope/30',
    outline: 'bg-transparent text-isotope border-isotope/55',
    solid: 'bg-isotope text-content border-isotope',
  },
  power: {
    soft: 'bg-power/12 text-power-bright border-power/30',
    outline: 'bg-transparent text-power border-power/55',
    solid: 'bg-power text-content-inverse border-power',
  },
  aether: {
    soft: 'bg-aether/12 text-aether-bright border-aether/30',
    outline: 'bg-transparent text-aether border-aether/55',
    solid: 'bg-aether text-content border-aether',
  },
  credit: {
    soft: 'bg-credit/12 text-credit-bright border-credit/30',
    outline: 'bg-transparent text-credit border-credit/55',
    solid: 'bg-credit text-content-inverse border-credit',
  },
  chronos: {
    soft: 'bg-chronos/12 text-chronos-bright border-chronos/30',
    outline: 'bg-transparent text-chronos border-chronos/55',
    solid: 'bg-chronos text-content border-chronos',
  },
  danger: {
    soft: 'bg-danger/12 text-danger-bright border-danger/30',
    outline: 'bg-transparent text-danger border-danger/55',
    solid: 'bg-danger text-content border-danger',
  },
  warning: {
    soft: 'bg-warning/12 text-warning-bright border-warning/30',
    outline: 'bg-transparent text-warning border-warning/55',
    solid: 'bg-warning text-content-inverse border-warning',
  },
  success: {
    soft: 'bg-success/12 text-success-bright border-success/30',
    outline: 'bg-transparent text-success border-success/55',
    solid: 'bg-success text-content-inverse border-success',
  },
  info: {
    soft: 'bg-info/12 text-info-bright border-info/30',
    outline: 'bg-transparent text-info border-info/55',
    solid: 'bg-info text-content-inverse border-info',
  },
  neutral: {
    soft: 'bg-surface-raised text-content-secondary border-edge',
    outline: 'bg-transparent text-content-muted border-edge',
    solid: 'bg-obsidian-300 text-content-inverse border-obsidian-300',
  },
};

/** Compact status label — levels, ring zones, mission types, fleet states. */
export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(function Badge(
  { accent = 'neutral', variant = 'soft', size = 'md', icon, pulse = false, className, children, ...rest },
  ref,
) {
  return (
    <span
      ref={ref}
      className={cn(
        'inline-flex items-center gap-1 rounded-control border font-display font-medium uppercase',
        'tracking-[0.1em] whitespace-nowrap',
        size === 'sm' ? 'h-[18px] px-1.5 text-label-sm' : 'h-[22px] px-2 text-label',
        ACCENTS[accent][variant],
        className,
      )}
      {...rest}
    >
      {pulse ? (
        <span
          aria-hidden
          className="h-1.5 w-1.5 animate-pulse-glow rounded-full bg-current"
        />
      ) : null}
      {icon}
      {children}
    </span>
  );
});
